import { usageTracker, UsageTrackerEvent } from "@volumental/analytic";
import type { Size } from "@volumental/common";
import { sendEvent } from "@volumental/link";
import { CombinedGraphQLErrors } from "@apollo/client";
import type { ErrorLike } from "@apollo/client";
import { SizeLocale } from "../api/gql/graphql";
import type {
  SizeChartQueryFragmentFragment,
  StyleMetaDataQueryFragmentFragment,
  RecommendationQueryFragmentFragment,
  SizesWithFootRangeFragmentFragment,
} from "../api/gql/graphql";
import { DisplayUnit, toSizeRecommendation } from "../types/types";
import type { RecommendationError, ScanResult, SizeChartData, SizeRecommendation } from "../types/types";

type GqlSizeRecommendation = NonNullable<RecommendationQueryFragmentFragment["sizeRecommendation"]>;
type SizeChart = NonNullable<SizeChartQueryFragmentFragment["sizeChart"]>;
type StyleMetaData = NonNullable<StyleMetaDataQueryFragmentFragment["styleMetaData"]>;
type SizesWithFootRange = SizesWithFootRangeFragmentFragment;

export const handleRecommendationResult = (
  variables: {
    productID: string;
    brandName?: string | null | undefined;
    genderAge?: string | null | undefined;
    scanID?: string | null | undefined;
    previousPurchaseID?: string | null | undefined;
  },
  data?: { sizeRecommendation?: GqlSizeRecommendation | null | undefined },
  error?: ErrorLike,
  widgetComponentType?: string
): { recommendation?: SizeRecommendation; error?: RecommendationError } | undefined => {
  const gqlErrors = CombinedGraphQLErrors.is(error)
    ? error.errors.map((e) => ({
        code: e.extensions?.code as string,
        message: e.message,
      }))
    : undefined;
  const recError = gqlErrors?.find((e) => e.code === "SIZE_NOT_AVAILABLE") ?? gqlErrors?.at(0);
  const rec = toSizeRecommendation(data?.sizeRecommendation);
  const result =
    rec || recError
      ? {
          recommendation: rec,
          error: recError,
        }
      : undefined;
  if (result) {
    const { productID, scanID, previousPurchaseID } = variables;
    sendSizeRecommendation(
      productID,
      {
        scanId: scanID ?? undefined,
        previousPurchaseID: previousPurchaseID ?? undefined,
      },
      result.recommendation,
      result.error,
      widgetComponentType
    );
  }
  return result;
};

export const sendSizeRecommendation = (
  productId: string,
  input: {
    scanId?: string;
    previousPurchaseID?: string;
  },
  recommendation?: SizeRecommendation,
  error?: RecommendationError,
  widgetComponentType?: string
): void => {
  const rec = recommendation
    ? {
        length: recommendation.lengthSize,
        width: recommendation.widthSize,
        sizeLocale: recommendation.sizeLocale as string,
        predictorLevel: recommendation.predictorLevel,
      }
    : undefined;

  const score = recommendation?.fitScores?.find(
    (f) => f.lengthSize === recommendation.lengthSize && f.widthSize === recommendation.widthSize
  )?.score;

  if (rec) {
    usageTracker.track(UsageTrackerEvent.ShowRecommendation, {
      scan_id: input.scanId,
      productID: productId,
      previousPurchaseID: input.previousPurchaseID,
      length: rec.length,
      width: rec.width,
      predictor_level: rec.predictorLevel,
      widget_component_type: widgetComponentType,
      fit_score: score,
    });
    sendEvent({
      type: "recommendation",
      data: {
        recommendation: rec,
        scanId: input.scanId,
        previousPurchaseID: input.previousPurchaseID,
      },
    });
  } else {
    const err = error ?? { code: "500", message: "Unknown error" };
    usageTracker.track(UsageTrackerEvent.ShowError, {
      error_message: err.message,
    });
    sendEvent({
      type: "recommendation",
      data: {
        error: err,
        scanId: input.scanId,
        previousPurchaseID: input.previousPurchaseID,
      },
    });
  }
};

export const sendMeasurement = (scan: ScanResult): void => {
  sendEvent({
    type: "scan",
    data: {
      id: scan.id,
      webScanId: scan.publicID,
    },
  });
};

export const ConvertMeterstoDisplayUnits = (value: number, displayUnit: DisplayUnit): number => {
  switch (displayUnit) {
    case DisplayUnit.Centimeters: {
      return Number((value * 100).toFixed(1));
    }
    case DisplayUnit.Inches: {
      return Number((value * 39.3701).toFixed(2));
    }
  }
};

export const RemoveSizingGender = (sizeLocale: SizeLocale): SizeLocale =>
  sizeLocale === SizeLocale.UsMen || sizeLocale === SizeLocale.UsWomen ? SizeLocale.Us : sizeLocale;

export const GetSizingGender = (sizeLocale: SizeLocale): "MEN" | "WOMEN" | undefined => {
  if (sizeLocale === SizeLocale.UsMen) {
    return "MEN";
  }
  if (sizeLocale === SizeLocale.UsWomen) {
    return "WOMEN";
  }
  return undefined;
};

export const createSizeList = (sizes: SizesWithFootRange[] | undefined | null): Size[] => {
  if (!sizes) {
    return [];
  }
  const sizeList: Size[] = [];
  for (const size of sizes) {
    if (size.localizedSizes) {
      sizeList.push({
        footLength: size.footLength,
        footWidth: size.footWidth ?? undefined,
        localizedSizes: size.localizedSizes,
      });
    }
  }
  return sizeList;
};

export const createSizeChartData = (
  sizeChart?: SizeChart | null,
  styleData?: StyleMetaData | null
): SizeChartData | undefined => {
  if (sizeChart?.predictorLevel && styleData && sizeChart.allCharts && sizeChart.allCharts.length > 0) {
    const sizeData: SizeChartData = {
      shoe: styleData,
      allCharts: sizeChart.allCharts.map((chart) => ({
        genderAge: chart.genderAge,
        sizes: createSizeList(chart.sizes),
      })),
      predictorLevel: sizeChart.predictorLevel,
    };

    return sizeData;
  }
  return undefined;
};

export const getProductName = (styleData: StyleMetaData, predictorLevel?: string): string => {
  if (styleData.styleName && (predictorLevel === "style" || predictorLevel === "style_keep_rate")) {
    return `${styleData.brandName} ${styleData.styleName}`;
  }
  return styleData.brandName;
};
